import { useContext } from 'react'
import { useApolloClient } from '@apollo/client'
import { useSelector } from 'react-redux'
import { userStore } from '../utils/UserContext'
import { searchPromoCode } from '../../graphql/PromoCode/searchPromoCode'
import { searchUserCodeRedemption } from '../../graphql/UserCodeRedemption/searchUserCodeRedemption'
import { PromoCodeType } from '../../types/PromoCodeType'
import { AppState } from '../../redux/root-reducer'
import { ChallengeState } from '../../redux/challenge/types'

const usePromoCode = () => {
  const apollo = useApolloClient()
  const { userChallenge } = useSelector<AppState, ChallengeState>((state) => state.challenge)
  const { user } = useContext(userStore)

  // Checks if the code is valid and applicable to the user
  const validatePromoCode = async (promoCode: string) => {
    const { data } = await apollo.query({
      query: searchPromoCode,
      variables: { code: promoCode }
    })

    const promo = data.promoCodeFindOne as PromoCodeType

    if (!promo) {
      throw new Error('Invalid code.')
    }

    if (!canUserUseCode(promo.whoCanUse)) {
      throw new Error('Invalid subscription type.')
    }

    const alreadyUsed = await userUsedCode(promo.stripeID)

    if (alreadyUsed) {
      throw new Error('Already used.')
    }

    return promo
  }

  // Returns true if the user already used the code
  const userUsedCode = async (stripeID: string) => {
    try {
      const { data } = await apollo.query({
        fetchPolicy: 'no-cache',
        query: searchUserCodeRedemption,
        variables: { user: user?.id, code: stripeID }
      })

      const userCodeRedemptions = data.UserCodeRedemptionFindMany

      if (userCodeRedemptions.length) {
        return true
      }

      return false
    }
    catch (err) {
      console.log(err.message)
      return false
    }
  }

  // Returns true if the user's subscription matches the promo's details
  const canUserUseCode = (
    whoCanUse: ('Month' | 'Year' | 'Forever' | 'Trial' | 'Challenge' | 'Inactive')[]
  ) => {
    if (whoCanUse.includes('Forever') && user?.forever) {
      return true
    }

    if (whoCanUse.includes('Challenge') && !!userChallenge) {
      return true
    }

    if (whoCanUse.includes('Inactive') && user?.stripeSubscriptionStatus === 'Inactive') {
      return true
    }

    if (whoCanUse.includes('Trial') && user?.trial) {
      return true
    }

    if (whoCanUse.includes(user?.subscriptionInterval!) && !user?.forever) {
      return true
    }

    return false
  }

  return { validatePromoCode }
}

export default usePromoCode
